import { useMutation } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";

const extractId = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") return value.id || value._id || "";
  return String(value);
};

/**
 * All mutations related to sales (create, update, delete, mark-defaulted, delete-defaulted).
 * @param {Object} options
 * @param {Object} options.queryClient       - React Query client for invalidation
 * @param {Array}  options.apartments        - Current apartments list (needed by markDefaulted)
 * @param {Function} options.onSaleFormClose - Called on create/update success to close the form
 */
export function useSalesMutations({ queryClient, apartments, onSaleFormClose }) {
  // ------------------------------------------------------------
  // CREATE SALE (supports single or multi-apartment batch)
  // ------------------------------------------------------------
  const createSaleMutation = useMutation({
    mutationFn: async (data) => {
      const salesArray = Array.isArray(data) ? data : [data];
      const salesCreated = [];
      
      for (const saleData of salesArray) {
        // ✅ Safely extract apartment_id
        const aptId = extractId(saleData.apartment_id);
        
        // 1. Create the sale
        // client_id can also arrive as a populated object (e.g. when this
        // mutation is called from an "undo delete" restore using a sale
        // fetched from the API), so extract it the same way as apartment_id.
        const sale = await base44.entities.Sale.create({
          ...saleData,
          apartment_id: aptId, // Ensure we save the string to the DB
          client_id: extractId(saleData.client_id),
        });
        salesCreated.push(sale);

        // 2. Mark apartment as sold
        if (aptId) {
          await base44.entities.Apartment.update(aptId, { status: "sold" });
        }

        // 3. If there's an advanced payment, create a Payment record
        if (saleData.first_installment > 0) {
          await base44.entities.Payment.create({
            sale_id: sale.id,
            payment_date: saleData.sale_date,
            amount: saleData.first_installment,
            currency: saleData.currency,
            payment_method: saleData.payment_method,
            payment_type: "advanced_payment",
            reference_number: "Advanced Payment",
            notes: "",
          });

          // 4. Update sale's total_paid (and status if fully paid)
          const totalPrice = saleData.total_price || 0;
          const updateData = { total_paid: saleData.first_installment };
          if (saleData.first_installment >= totalPrice) updateData.status = "completed";
          await base44.entities.Sale.update(sale.id, updateData);
        }
      }
      
      return salesCreated;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sales"] });
      queryClient.invalidateQueries({ queryKey: ["apartments"] });
      queryClient.invalidateQueries({ queryKey: ["payments"] });
      onSaleFormClose?.();
      if (window.addNotification) {
        window.addNotification("Sale(s) created successfully", "success");
      }
    },
    onError: (error) => {
      if (window.addNotification) {
        window.addNotification(error?.message || "Failed to create sale", "error");
      } else {
        alert(error?.message || "Failed to create sale");
      }
    },
  });

  // ------------------------------------------------------------
  // UPDATE SALE (handles apartment swap if apartment_id changed)
  // ------------------------------------------------------------
  const updateSaleMutation = useMutation({
    mutationFn: async ({ id, data, originalApartmentId }) => {
      // ✅ Safely extract IDs
      const newAptId = extractId(data.apartment_id);
      const oldAptId = extractId(originalApartmentId);
      
      const updatedSale = await base44.entities.Sale.update(id, {
        ...data,
        apartment_id: newAptId, // Ensure we save the string
        client_id: extractId(data.client_id), // Same fix as apartment_id above
      });

      // If the apartment was changed, swap the statuses
      if (oldAptId && newAptId && oldAptId !== newAptId) {
        await base44.entities.Apartment.update(oldAptId, { status: "available" });
        await base44.entities.Apartment.update(newAptId, { status: "sold" });
      }
      
      return updatedSale;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sales"] });
      queryClient.invalidateQueries({ queryKey: ["apartments"] });
      queryClient.invalidateQueries({ queryKey: ["client-sales"] });
      queryClient.invalidateQueries({ queryKey: ["payments"] });
      onSaleFormClose?.();
      if (window.addNotification) {
        window.addNotification("Sale updated successfully", "success");
      }
    },
    onError: (error) => {
      if (window.addNotification) {
        window.addNotification(error?.message || "Failed to update sale", "error");
      } else {
        alert(error?.message || "Failed to update sale");
      }
    },
  });

   // ------------------------------------------------------------
  // DELETE SALE (server archives the sale, its payments/receipts, and
  // frees the apartment — one request instead of a per-record cascade)
  // ------------------------------------------------------------
  const deleteSaleMutation = useMutation({
    mutationFn: (sale) => base44.entities.Sale.delete(sale.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sales"] });
      queryClient.invalidateQueries({ queryKey: ["apartments"] });
      queryClient.invalidateQueries({ queryKey: ["payments"] });
      queryClient.invalidateQueries({ queryKey: ["receipts"] });
      if (window.addNotification) {
        window.addNotification("Sale deleted and apartment freed", "success");
      }
    },
  });

  // ------------------------------------------------------------
  // MARK SALE AS DEFAULTED
  // Cascade: create DefaultedSale → delete payments/receipts → free apartment → delete sale
  // ------------------------------------------------------------
  const markDefaultedMutation = useMutation({
    mutationFn: async (sale) => {
      const reason = prompt("Enter reason for defaulting this sale (optional):");
      
      // ✅ Safely extract apartment_id
      const aptId = extractId(sale.apartment_id);
      const apartment = apartments.find((a) => a.id === aptId);

      // 1. Archive as DefaultedSale
      await base44.entities.DefaultedSale.create({
        original_sale_id: sale.id,
        apartment_id: aptId, // ✅ Use the safely extracted ID
        unit_number: apartment?.unit_number || "",
        client_id: extractId(sale.client_id),
        contract_number: sale.contract_number,
        contract_document_url: sale.contract_document_url,
        sale_date: sale.sale_date,
        defaulted_date: new Date().toISOString().split("T")[0],
        total_price: sale.total_price,
        currency: sale.currency,
        first_installment: sale.first_installment,
        monthly_payment: sale.monthly_payment,
        payment_duration_months: sale.payment_duration_months,
        payment_frequency_months: sale.payment_frequency_months,
        total_paid: sale.total_paid,
        payment_method: sale.payment_method,
        default_reason: reason || "",
        notes: sale.notes,
      });

      // 2. Delete related payments and receipts
      const allPayments = await base44.entities.Payment.list();
      const allReceipts = await base44.entities.Receipt.list();
      
      // ✅ Safe comparison
      const relatedPayments = allPayments.filter((p) => extractId(p.sale_id) === sale.id);
      
      for (const payment of relatedPayments) {
        const paymentReceipts = allReceipts.filter((r) => extractId(r.payment_id) === payment.id);
        for (const receipt of paymentReceipts) {
          await base44.entities.Receipt.delete(receipt.id);
        }
        await base44.entities.Payment.delete(payment.id);
      }

      // 3. Free the apartment
      if (aptId) {
        await base44.entities.Apartment.update(aptId, { status: "available" });
      }

      // 4. Delete the original sale
      await base44.entities.Sale.delete(sale.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sales"] });
      queryClient.invalidateQueries({ queryKey: ["defaultedSales"] });
      queryClient.invalidateQueries({ queryKey: ["apartments"] });
      queryClient.invalidateQueries({ queryKey: ["payments"] });
      queryClient.invalidateQueries({ queryKey: ["receipts"] });
      if (window.addNotification) {
        window.addNotification("Sale marked as defaulted successfully", "success");
      }
    },
  });

  // ------------------------------------------------------------
  // DELETE DEFAULTED SALE (simple single-entity delete)
  // ------------------------------------------------------------
  const deleteDefaultedSaleMutation = useMutation({
    mutationFn: async (defaultedSaleId) => {
      await base44.entities.DefaultedSale.delete(defaultedSaleId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["defaultedSales"] });
    },
  });

  return {
    createSaleMutation,
    updateSaleMutation,
    deleteSaleMutation,
    markDefaultedMutation,
    deleteDefaultedSaleMutation,
  };
}